import { Dispatch, createContext, useReducer } from "react"
import { Expression, parseExpression } from "tadiff"

export class AppEnergyTerm {
    constructor(public expression: Expression, public expressionFormula: string,
        public affectedObjects: { [objectIndex: number]: boolean },
        public affectedObjectPairs: { [objectIndices: string]: boolean }) {
    }
}

export type AppObjectProperty = {
    name: string
    defaultValue: number
}

export type AppObject = {
    propertyValues: { [propertyName: string]: number }
}

export type AppState = {
    energyTerms: AppEnergyTerm[]
    objects: AppObject[]
    dimensions: number
    initialPositionScale: number
    initialMomentumScale: number
    objectProperties: AppObjectProperty[]
}


export type AppActionAddEnergyTerm = {
    type: "addEnergyTerm"
    term: AppEnergyTerm
}

export type AppActionUpdateEnergyTerm = {
    type: "updateEnergyTerm"
    index: number
    term: AppEnergyTerm
}

export type AppActionRemoveEnergyTerm = {
    type: "removeEnergyTerm"
}

export type AppActionAddObject = {
    type: "addObject"
}

export type AppActionUpdateObject = {
    type: "updateObject"
    index: number
    object: AppObject
}

export type AppActionRemoveObject = {
    type: "removeObject"
}

export type AppActionAddObjectProperty = {
    type: "addObjectProperty"
}

export type AppActionUpdateObjectProperty = {
    type: "updateObjectProperty"
    name: string
    defaultValue: number
}

export type AppActionRemoveObjectProperty = {
    type: "removeObjectProperty"
}

export type AppActionSetInitialScales = {
    type: "setInitialScales"
    positionScale: number
    momentumScale: number
}

export type AppActionLoadState = {
    type: "loadState"
    state: AppState
}

export type AppActionLoadPreset = {
    type: "loadPreset"
    preset: "gravity" | "coulomb" | "attractor" | "nuclear"
}

export type AppAction = AppActionAddEnergyTerm | AppActionUpdateEnergyTerm |
    AppActionRemoveEnergyTerm | AppActionAddObject | AppActionRemoveObject |
    AppActionSetInitialScales | AppActionLoadState | AppActionAddObjectProperty |
    AppActionRemoveObjectProperty | AppActionUpdateObjectProperty | AppActionUpdateObject |
    AppActionLoadPreset

function getPresetStateGravity(): AppState {
    const kineticEnergyFormula = "0.5 * (p0^2 + p1^2) / m"
    const gravityFormula = "-1 * m * m_b / sqrt((q0 - q0_b)^2 + (q1 - q1_b)^2)"

    const state: AppState = {
        energyTerms: [{
            expression: parseExpression(kineticEnergyFormula),
            expressionFormula: kineticEnergyFormula,
            affectedObjects: {},
            affectedObjectPairs: {}
        }, {
            expression: parseExpression(gravityFormula),
            expressionFormula: gravityFormula,
            affectedObjects: {},
            affectedObjectPairs: {}
        }],
        objects: [],
        dimensions: 2,
        initialPositionScale: 100,
        initialMomentumScale: 50,
        objectProperties: [{
            name: "m",
            defaultValue: 1
        }]
    }

    for (let i = 0; i < 8; i++) {
        state.objects.push({
            propertyValues: {
                "m": i === 0 ? 10000 : i
            }
        })
    }

    state.objects.forEach((_, objectIndex) => {
        state.energyTerms[0].affectedObjects[objectIndex] = true
    })

    state.objects.forEach((_, objectIndex) => {
        state.objects.forEach((_, otherObjectIndex) => {
            if (objectIndex !== otherObjectIndex) {
                state.energyTerms[1].affectedObjectPairs[[objectIndex, otherObjectIndex].toString()] = true
            }
        })
    })

    return state
}

function getPresetStateCoulomb(): AppState {
    const kineticEnergyFormula = "0.5 * (p0^2 + p1^2) / m"
    const coulombFormula = "10000 * Q * Q_b / sqrt((q0 - q0_b)^2 + (q1 - q1_b)^2)"

    const state: AppState = {
        energyTerms: [{
            expression: parseExpression(kineticEnergyFormula),
            expressionFormula: kineticEnergyFormula,
            affectedObjects: {},
            affectedObjectPairs: {}
        }, {
            expression: parseExpression(coulombFormula),
            expressionFormula: coulombFormula,
            affectedObjects: {},
            affectedObjectPairs: {}
        }],
        objects: [],
        dimensions: 2,
        initialPositionScale: 50,
        initialMomentumScale: 0,
        objectProperties: [{
            name: "Q",
            defaultValue: 1
        }, {
            name: "m",
            defaultValue: 1
        }]
    }

    for (let i = 0; i < 8; i++) {
        state.objects.push({
            propertyValues: {
                "Q": i % 2 === 0 ? 1 : -1
            }
        })
    }

    state.objects.forEach((_, objectIndex) => {
        state.energyTerms[0].affectedObjects[objectIndex] = true
    })

    state.objects.forEach((_, objectIndex) => {
        state.objects.forEach((_, otherObjectIndex) => {
            if (objectIndex !== otherObjectIndex) {
                state.energyTerms[1].affectedObjectPairs[[objectIndex, otherObjectIndex].toString()] = true
            }
        })
    })

    return state
}

function getPresetStateAttractor(): AppState {
    const formula = "0.5 * (q0^2 + q1^2) + q0 * (q0^2 - q1^2 / 3.0) + 0.5 * (p1^2 + p0^2)"

    const state: AppState = {
        energyTerms: [{
            expression: parseExpression(formula),
            expressionFormula: formula,
            affectedObjects: {},
            affectedObjectPairs: {}
        }],
        objects: [],
        dimensions: 2,
        initialPositionScale: 0.3,
        initialMomentumScale: 0.3,
        objectProperties: []
    }

    for (let i = 0; i < 10; i++) {
        state.objects.push({
            propertyValues: {}
        })
    }

    state.objects.forEach((_, objectIndex) => {
        state.energyTerms[0].affectedObjects[objectIndex] = true
    })

    return state
}

function getPresetStateNuclear(): AppState {
    const kineticEnergyFormula = "0.5 * (p0^2 + p1^2) / m"
    const attractFormula = "100 * Q * Q_b / sqrt((q0 - q0_b)^2 + (q1 - q1_b)^2)"
    const repelFormula = "300 * exp(-3 * sqrt((q0 - q0_b)^2 + (q1 - q1_b)^2)) / sqrt((q0 - q0_b)^2 + (q1 - q1_b)^2)"

    const state: AppState = {
        energyTerms: [{
            expression: parseExpression(kineticEnergyFormula),
            expressionFormula: kineticEnergyFormula,
            affectedObjects: {},
            affectedObjectPairs: {}
        }, {
            expression: parseExpression(attractFormula),
            expressionFormula: attractFormula,
            affectedObjects: {},
            affectedObjectPairs: {}
        }, {
            expression: parseExpression(repelFormula),
            expressionFormula: repelFormula,
            affectedObjects: {},
            affectedObjectPairs: {}
        }],
        objects: [],
        dimensions: 2,
        initialPositionScale: 10,
        initialMomentumScale: 0,
        objectProperties: [{
            name: "Q",
            defaultValue: 1
        }, {
            name: "m",
            defaultValue: 1
        }]
    }

    for (let i = 0; i < 8; i++) {
        state.objects.push({
            propertyValues: {
                "Q": (i % 2) === 1 ? 1 : -1,
                "m": (i % 2) === 1 ? 1800 : 1
            }
        })
    }

    state.objects.forEach((_, objectIndex) => {
        state.energyTerms[0].affectedObjects[objectIndex] = true
    })

    state.objects.forEach((_, objectIndex) => {
        state.objects.forEach((_, otherObjectIndex) => {
            if (objectIndex !== otherObjectIndex) {
                state.energyTerms[1].affectedObjectPairs[[objectIndex, otherObjectIndex].toString()] = true
                state.energyTerms[2].affectedObjectPairs[[objectIndex, otherObjectIndex].toString()] = true
            }
        })
    })

    return state
}

function appReducer(state: AppState, action: AppAction) {
    switch (action.type) {
        case "addEnergyTerm": {
            return { ...state, energyTerms: [...state.energyTerms, action.term] }
        } case "updateEnergyTerm": {
            const energyTerms = [...state.energyTerms]
            energyTerms[action.index] = action.term
            return { ...state, energyTerms: energyTerms }
        } case "removeEnergyTerm": {
            const energyTerms = [...state.energyTerms]
            energyTerms.pop()
            return { ...state, energyTerms: energyTerms }
        } case "addObject": {
            const newObject: AppObject = { propertyValues: {} }
            return { ...state, objects: [...state.objects, newObject] }
        } case "updateObject": {
            const objects = [...state.objects]
            objects[action.index] = action.object
            return { ...state, objects: objects }
        } case "removeObject": {
            const objects = [...state.objects]
            objects.pop()
            return { ...state, objects: objects }
        } case "setInitialScales": {
            return {
                ...state,
                initialPositionScale: action.positionScale,
                initialMomentumScale: action.momentumScale
            }
        } case "loadState": {
            return action.state
        } case "addObjectProperty": {
            const objectProperties: AppObjectProperty[] = [
                ...state.objectProperties,
                { name: "x" + state.objectProperties.length.toString(), defaultValue: 0 }
            ]
            return {
                ...state,
                objectProperties: objectProperties
            }
        } case "removeObjectProperty": {
            const objectProperties = [...state.objectProperties]
            objectProperties.pop()
            return { ...state, objectProperties: objectProperties }
        } case "updateObjectProperty": {
            const objectProperties = [...state.objectProperties]
            objectProperties.forEach((objectProperty, index) => {
                if (objectProperty.name === action.name) {
                    objectProperties[index] = {
                        name: action.name,
                        defaultValue: action.defaultValue
                    }
                }
            })
            return { ...state, objectProperties: objectProperties }
        } case "loadPreset": {
            switch (action.preset) {
                case "coulomb":
                    return getPresetStateCoulomb()
                case "gravity":
                    return getPresetStateGravity()
                case "attractor":
                    return getPresetStateAttractor()
                case "nuclear":
                    return getPresetStateNuclear()
                default:
                    throw new Error("Unknown preset " + action.preset)
            }
        }
    }

    throw new Error("Unhandled action in reducer")
}

export function useAppReducer() {
    return useReducer(appReducer, getPresetStateAttractor())
}

export const AppContext = createContext<{ state: AppState, dispatch: Dispatch<AppAction> } | undefined>(undefined)